<template>
  <div
    ref="root"
    class="c-month-picker"
    :class="classes"
    :data-testid="`month-picker-${resolvedFieldName}`"
  >
    <div class="border" />

    <div
      class="value"
      tabindex="0"
      @click="toggleOpened"
    >
      <c-flag
        v-if="icon"
        grey
        class="flag"
        size="30"
        icon-size="18"
        :icon="icon"
      />

      <c-flag
        v-if="showStateIcon"
        class="icon-alert-month"
        validation
        :alert="alertDateColor"
      />

      <input
        v-if="enableInput && mode === 'single'"
        ref="input"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        class="text"
        :class="inputClasses"
        :value="inputValue"
        :placeholder="placeholder"
        :data-testid="`month-picker-input-${resolvedFieldName}`"
        @click.stop
        @input.stop="onInput"
        @keyup.enter.stop.prevent
        @keydown.enter.stop.prevent
        @keypress.enter.stop.prevent
      />

      <transition name="fade" mode="out-in">
        <span
          v-if="!enableInput || mode !== 'single'"
          :key="displayedValue"
          class="text"
          :class="textClasses"
        >
          {{ displayedValue }}
        </span>
      </transition>

      <c-icon
        class="select-icon"
        icon="arrow-right-2"
        size="24"
      />
    </div>

    <div class="wrapper">
      <transition name="content">
        <c-month-picker-panel
          v-if="opened"
          :can-go-prev-year="canGoPrevYear"
          :can-go-next-year="canGoNextYear"
          :current-year="currentYear"
          :months="months"
          :resolved-field-name="resolvedFieldName"
          :get-month-classes="getMonthClasses"
          :is-month-disabled="isMonthDisabled"
          :show-current-month-indicator="showCurrentMonthIndicator"
          @reset="onReset"
          @prev-year="currentYear -= 1"
          @next-year="currentYear += 1"
          @select-month="onSelectMonth"
        />
      </transition>
    </div>

    <div
      v-if="displayedMessage"
      class="message"
      :class="messageClasses"
      :data-testid="`month-picker-message-${resolvedFieldName}`"
    >
      <c-flag
        validation
        :alert="alertDateColor"
      />

      <span class="message-text">
        {{ displayedMessage }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { dayjs } from '@convenia/utils'
import CFlag from '@convenia/components/CFlag'
import CIcon from '@convenia/components/CIcon'
import CMonthPickerPanel from './fragments/CMonthPickerPanel.vue'
import {
  MONTHS,
  MONTH_STRING_REGEX,
  NON_DIGIT_OR_SLASH_REGEX,
  YEAR_MONTH_STRING_REGEX
} from './constants'
import {
  compareMonth,
  createMonthDate,
  formatMonth,
  isSameMonth,
  normalizeInputValue,
  parseMonthDate,
  parseMonthString,
  parseYearMonthString
} from './utils'

const props = defineProps({
  icon: {
    type: String,
    default: 'calendar-schedule-2'
  },

  placeholder: {
    type: String,
    default: '00/0000'
  },

  value: {
    type: [ Object, String, Date ],
    default: undefined
  },

  modelValue: {
    type: [ Object, String, Date ],
    default: undefined
  },

  startDate: {
    type: [ String, Date ],
    default: null
  },

  endDate: {
    type: [ String, Date ],
    default: null
  },

  minDate: Date,
  maxDate: Date,

  mode: {
    type: String,
    default: 'single',
    validator: mode => [ 'range', 'single' ].includes(mode)
  },

  disabled: Boolean,
  error: Boolean,
  expiredDate: Boolean,
  alertDateColor: Boolean,
  enableInput: Boolean,
  maxRange: Boolean,

  fieldName: {
    type: String,
    default: ''
  },

  message: {
    type: String,
    default: ''
  },

  invalidMessage: {
    type: String,
    default: 'Data inválida.'
  }
})

const emit = defineEmits([ 'input', 'update:modelValue', 'focus', 'blur' ])

const root = ref(null)
const opened = ref(false)
const inputChanged = ref(false)
const inputValue = ref('')
const internalValidationMessage = ref('')
const internalValue = ref(null)
const rangeAnchor = ref(null)
const currentYear = ref(dayjs().year())

const months = MONTHS

const resolvedFieldName = computed(() => props.fieldName || 'default')
const externalValue = computed(() => (
  props.modelValue !== undefined ? props.modelValue : props.value
))

const displayedMessage = computed(() => props.message || internalValidationMessage.value)
const hasError = computed(() => props.error || !!internalValidationMessage.value)

const classes = computed(() => ({
  '-opened': opened.value,
  '-disabled': props.disabled,
  '-has-icon': props.icon,
  '-validation': hasError.value,
  '-expired': props.expiredDate,
  '-alert-message': props.alertDateColor,
  '-with-message': !!displayedMessage.value
}))

const inputClasses = computed(() => ({
  '-placeholder': !inputValue.value,
  '-input': true,
  '-expired': hasError.value || props.expiredDate,
  '-alert-message': props.alertDateColor
}))

const hasCompleteRange = computed(() => {
  if (props.mode !== 'range') return false
  return !!internalValue.value?.start && !!internalValue.value?.end
})

const hasPlaceholder = computed(() => {
  if (props.mode === 'range') {
    return !hasCompleteRange.value
  }

  return !internalValue.value
})

const textClasses = computed(() => ({
  '-placeholder': hasPlaceholder.value,
  '-expired': hasError.value || props.expiredDate,
  '-alert-message': props.alertDateColor
}))

const messageClasses = computed(() => ({
  '-alert-message': props.alertDateColor,
  '-expired': hasError.value || props.expiredDate
}))

const showStateIcon = computed(() => hasError.value || props.expiredDate || props.alertDateColor)

const parsedStartDate = computed(() => {
  if (!props.startDate) return null

  if (typeof props.startDate === 'string' && MONTH_STRING_REGEX.test(props.startDate)) {
    return parseMonthString(props.startDate)
  }

  if (typeof props.startDate === 'string' && YEAR_MONTH_STRING_REGEX.test(props.startDate)) {
    return parseYearMonthString(props.startDate)
  }

  return parseMonthDate(props.startDate)
})

const parsedEndDate = computed(() => {
  if (!props.endDate) return null

  if (typeof props.endDate === 'string' && MONTH_STRING_REGEX.test(props.endDate)) {
    return parseMonthString(props.endDate)
  }

  if (typeof props.endDate === 'string' && YEAR_MONTH_STRING_REGEX.test(props.endDate)) {
    return parseYearMonthString(props.endDate)
  }

  return parseMonthDate(props.endDate)
})

const emitValue = (value) => {
  emit('input', value)
  emit('update:modelValue', value)
}

const getInvalidMessage = () => props.invalidMessage || 'Data inválida.'

const setInternalValidationMessage = (message = '') => {
  internalValidationMessage.value = message
}

const syncCurrentYear = (value) => {
  if (props.mode === 'range') {
    const anchor = value?.start || value?.end || rangeAnchor.value
    const year = anchor ? dayjs(anchor).year() : dayjs().year()

    currentYear.value = Number.isFinite(year) ? year : dayjs().year()
    return
  }

  if (!value && props.maxRange && parsedStartDate.value) {
    currentYear.value = dayjs(parsedStartDate.value).year()
    return
  }

  const year = value ? dayjs(value).year() : dayjs().year()

  currentYear.value = Number.isFinite(year) ? year : dayjs().year()
}

const setInputValue = () => {
  inputValue.value = props.mode === 'single' && internalValue.value
    ? formatMonth(internalValue.value)
    : ''
}

const setInternalValue = (value) => {
  rangeAnchor.value = null

  if (props.mode === 'range' && value?.start && value?.end) {
    internalValue.value = {
      start: parseMonthDate(value.start) || parseYearMonthString(value.start),
      end: parseMonthDate(value.end) || parseYearMonthString(value.end)
    }
    syncCurrentYear(internalValue.value)
    inputChanged.value = false
    setInternalValidationMessage()
    return
  }

  if (typeof value === 'string' && MONTH_STRING_REGEX.test(value)) {
    internalValue.value = parseMonthString(value)
  } else if (typeof value === 'string' && YEAR_MONTH_STRING_REGEX.test(value)) {
    internalValue.value = parseYearMonthString(value)
  } else if (value && typeof value === 'object' && (value.start || value.end)) {
    internalValue.value = null
  } else {
    internalValue.value = parseMonthDate(value) || null
  }

  if (!inputChanged.value) setInputValue()

  syncCurrentYear(internalValue.value)
  inputChanged.value = false
  setInternalValidationMessage()
}

const isMonthWithinBounds = (date) => {
  if (!date) return false

  const normalized = dayjs(date).startOf('month')
  const normalizedStartDate = parsedStartDate.value
    ? dayjs(parsedStartDate.value).startOf('month')
    : null
  const normalizedEndDate = parsedEndDate.value
    ? dayjs(parsedEndDate.value).startOf('month')
    : null

  if (props.minDate && normalized.isBefore(dayjs(props.minDate).startOf('month'))) return false
  if (props.maxDate && normalized.isAfter(dayjs(props.maxDate).startOf('month'))) return false
  if (normalizedStartDate && !normalized.isAfter(normalizedStartDate)) return false
  if (normalizedEndDate && !normalized.isBefore(normalizedEndDate)) return false

  return true
}

const yearHasSelectableMonth = (year) => {
  if (props.disabled) return false

  return months.some((_, index) => isMonthWithinBounds(createMonthDate(year, index + 1)))
}

const displayedValue = computed(() => {
  if (props.mode === 'range') {
    if (!hasCompleteRange.value) return props.placeholder

    const start = formatMonth(internalValue.value.start)
    const end = formatMonth(internalValue.value.end)

    return `${start} - ${end}`
  }

  if (!internalValue.value) return props.placeholder

  return formatMonth(internalValue.value)
})

const canGoPrevYear = computed(() => yearHasSelectableMonth(currentYear.value - 1))
const canGoNextYear = computed(() => yearHasSelectableMonth(currentYear.value + 1))

const isMonthDisabled = (index) => {
  if (props.disabled) return true

  return !isMonthWithinBounds(createMonthDate(currentYear.value, index + 1))
}

const getRangeState = (date) => {
  if (props.mode !== 'range') return {}

  if (rangeAnchor.value && !hasCompleteRange.value) {
    return {
      '-selected': isSameMonth(date, rangeAnchor.value)
    }
  }

  if (!hasCompleteRange.value) return {}

  const { start, end } = internalValue.value
  const startEqualsEnd = isSameMonth(start, end)

  return {
    '-selected': startEqualsEnd && isSameMonth(date, start),
    '-range-start': !startEqualsEnd && isSameMonth(date, start),
    '-range-end': !startEqualsEnd && isSameMonth(date, end),
    '-range-middle': compareMonth(date, start) > 0 && compareMonth(date, end) < 0
  }
}

const getMonthClasses = (index) => {
  const date = createMonthDate(currentYear.value, index + 1)

  return {
    '-disabled': isMonthDisabled(index),
    '-selected': props.mode === 'single' && isSameMonth(date, internalValue.value),
    ...getRangeState(date)
  }
}

const showCurrentMonthIndicator = (index) => {
  const now = dayjs()
  const classes = getMonthClasses(index)

  return now.year() === currentYear.value
    && now.month() === index
    && !classes['-selected']
    && !classes['-range-start']
    && !classes['-range-end']
}

const onInput = (event) => {
  const rawValue = event?.target?.value || ''
  const value = normalizeInputValue(rawValue)

  inputValue.value = value
  setInternalValidationMessage()

  if (NON_DIGIT_OR_SLASH_REGEX.test(rawValue)) {
    setInternalValidationMessage(getInvalidMessage())
    return
  }

  if (value.length < 7) return

  const month = parseMonthString(value)

  if (!month || !isMonthWithinBounds(month)) {
    setInternalValidationMessage(getInvalidMessage())
    return
  }

  inputChanged.value = true
  internalValue.value = month
  syncCurrentYear(month)
  emitValue(month)
}

const onSelectMonth = (index) => {
  if (isMonthDisabled(index)) return

  const selectedMonth = createMonthDate(currentYear.value, index + 1)

  if (props.mode === 'single') {
    setInternalValidationMessage()
    internalValue.value = selectedMonth
    setInputValue()
    emitValue(selectedMonth)
    opened.value = false
    return
  }

  if (!rangeAnchor.value || hasCompleteRange.value) {
    rangeAnchor.value = selectedMonth
    internalValue.value = null
    return
  }

  const start = compareMonth(selectedMonth, rangeAnchor.value) < 0
    ? selectedMonth
    : rangeAnchor.value
  const end = compareMonth(selectedMonth, rangeAnchor.value) < 0
    ? rangeAnchor.value
    : selectedMonth

  internalValue.value = { start, end }
  rangeAnchor.value = null
  emitValue(internalValue.value)
  opened.value = false
}

const onReset = () => {
  internalValue.value = null
  rangeAnchor.value = null
  inputValue.value = ''
  setInternalValidationMessage()
  syncCurrentYear(null)
  emitValue(undefined)
}

const toggleOpened = () => {
  if (props.disabled) return

  opened.value = !opened.value
}

const onDocumentClick = (event) => {
  if (root.value?.contains(event.target)) return

  opened.value = false

  if (!inputChanged.value) {
    setInternalValue(externalValue.value)
    return
  }

  if (inputValue.value && inputValue.value.length < 7) {
    setInternalValidationMessage(getInvalidMessage())
  }

  inputChanged.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onDocumentClick)
  document.addEventListener('touchstart', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentClick)
  document.removeEventListener('touchstart', onDocumentClick)
})

watch(opened, (value) => {
  emit(value ? 'focus' : 'blur')
})

watch(externalValue, (value) => {
  setInternalValue(value)
}, { immediate: true })

watch(parsedStartDate, (startDate) => {
  if (!props.maxRange || internalValue.value || !startDate) return
  currentYear.value = dayjs(startDate).year()
})

</script>

<script>
export default {
  name: 'CMonthPicker',
}
</script>

<style lang="scss" scoped>
@import '../styles/mixins/calendar-picker-shell';

.c-month-picker {
  $content-height: 350px;
  $initial-height: 40px;
  $initial-border-radius: 20px;
  $open-border-radius: 5px;

  min-width: 361px;
  min-height: 40px;

  @include calendar-picker-shell(
    $content-height,
    $initial-height,
    $initial-border-radius,
    $open-border-radius,
    '.icon-alert-month'
  );

  &.-with-message {
    padding-bottom: 34px;
  }

  &.-opened > .border {
    height: 380px;
  }

  & > .value {
    & > .text {
      @include typo(body-4, base-50);

      &:not(.-input) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &.-placeholder,
      &.-input::placeholder {
        @include typo(body-1, base-30);
      }

      &.-input {
        width: 100%;
        border: 0;
        outline: 0;
        background: transparent;
        transition: color 200ms;

        &:hover,
        &:focus {
          color: color-var(text, base-80);
        }

        &.-expired {
          color: color-var(negative);
        }

        &.-expired::placeholder {
          color: color-var(negative);
        }

        &.-alert-message {
          color: color-var(alert);
        }

        &.-alert-message::placeholder {
          color: color-var(alert);
        }
      }

      &.-expired {
        color: color-var(negative);
      }

      &.-alert-message {
        color: color-var(alert);
      }
    }
  }

  & > .message {
    position: absolute;
    top: calc(#{$initial-height} + 8px);
    left: 4px;
    display: flex;
    align-items: flex-start;
    gap: 6px;
    color: color-var(negative);

    &.-alert-message {
      color: color-var(alert);
    }

    & > .message-text {
      @include typo(body-2);
      line-height: inherit;
      color: inherit;
    }
  }

  &.-alert-message:not(.-validation):not(.-opened) > .border {
    @include hover(color-var(alert));

    border-color: rgba(color-var(alert, base-rgb), .35);
    box-shadow: 0 0 0 1px rgba(color-var(alert, base-rgb)), 0 0 3px 1px rgba(color-var(alert, base-rgb), 0.35);
  }

  :deep(.flag > .c-icon) {
    position: relative;
    top: -1px;
  }

  :deep(.year-btn > .c-icon),
  :deep(.select-icon.c-icon) {
    filter: unset;
  }

  :deep(.message > .c-flag),
  :deep(.icon-alert-month.c-flag) {
    flex-shrink: 0;
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity .1s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
